import React, { useState, useEffect } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useNavigate } from "react-router-dom";

//API
import ItemService from "../../../services/supplier_appCommonService";

//Components
import { InputField } from "../../../Components/formElements/InputField";
import { ComboboxField } from "../../../Components/formElements/ComboboxField";
import { TextAreaField } from "../../../Components/formElements/TextAreaField";
import FileUploadComponent from "../../../Components/formElements/FileUploadComponent";
import FailureModal from "../../../Components/Modal/supplier_app/FailureModal";
import toast from "react-hot-toast";
import Layout from "../../../Layout/Layout";
import SupplierConstants from "../../../common/SupplierConstants";
import {
  isGeneralTemplate,
  getGeneralTemplateSections,
  getGeneralQuestions,
  getGeneralMandatoryQuestions,
  isGeneralMandatory,
  findGeneralQuestionSection,
} from "../../../Components/PQ/pqGeneralTemplateQuestions";

const message = {
  FILL_REQUIRED_FIELD: "Please fill the required field",
};

const baseSections = [
  { id: 1, key: "companyInformation", label: "Company Information", icon: "building" },
  { id: 2, key: "qualityManagement", label: "Quality Management", icon: "award" },
  { id: 3, key: "technicalCapability", label: "Technical Capability", icon: "cpu" },
  { id: 4, key: "deliveryCapability", label: "Delivery Capability", icon: "truck" },
  { id: 5, key: "complianceLegal", label: "Compliance & Legal", icon: "shield-check" },
  { id: 6, key: "financialInformation", label: "Financial Information", icon: "cash" },
  { id: 7, key: "commercialInformation", label: "Commercial Information", icon: "cart" },
  { id: 8, key: "experienceReferences", label: "Experience & References", icon: "briefcase" },
  { id: 9, key: "documentUpload", label: "Document Upload", icon: "file-earmark-arrow-up" },
  { id: 10, key: "declarationSubmit", label: "Declaration & Submit", icon: "check2-square" },
];

const defaultInvitation = {
  companyName: "ABC Engineering Pvt. Ltd.",
  category: "Electrical Components",
  subCategory: "Switched Accessories",
  template: "Electrical Components Supplier Qualification",
  email: "procurement@abcorg.com",
};

const VendorPreQualificationForm = () => {
  const navigate = useNavigate();

  localStorage.setItem("menu", "Vendor");
  localStorage.setItem("submenu", "Pre-Qualification");

  const failureModalData = {
    title: "Warning",
    ErrorMsg: "",
  };

  const [invitation, setInvitation] = useState(defaultInvitation);
  const [activeSection, setActiveSection] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [disabled, setDisabled] = useState(false);

  const [getDropdownData, setDropdownData] = useState("");
  const [getFailureModal] = useState(failureModalData);
  const [getShowFailureModal, setShowFailureModal] = useState(false);
  const [getErrorFields, setErrorFields] = useState([]);

  const [validation, setValidation] = useState({});

  /* 01 Company Information */
  const [companyInformation, setCompanyInformation] = useState({
    companyName: "",
    yearOfEstablishment: "",
    companyType: "",
    panNumber: "",
    gstNumber: "",
    website: "",
  });

  /* 02 Quality Management */
  const [qualityManagement, setQualityManagement] = useState({
    iso9001: "",
    isoCertificateNumber: "",
    qualityPolicy: "",
    qualityInspection: "",
    defectRate: "",
    qualityRemarks: "",
  });

  /* 03 Technical Capability */
  const [technicalCapability, setTechnicalCapability] = useState({
    machineryEquipment: "",
    productionCapacity: "",
    technicalTeamStrength: "",
    designCapability: "",
    technicalRemarks: "",
  });

  /* 04 Delivery Capability */
  const [deliveryCapability, setDeliveryCapability] = useState({
    deliveryLocations: "",
    averageLeadTime: "",
    warehouseCapacity: "",
    logisticsPartner: "",
    deliveryRemarks: "",
  });

  /* 05 Compliance & Legal */
  const [complianceLegal, setComplianceLegal] = useState({
    gstRegistered: "",
    tradeLicense: "",
    environmentalClearance: "",
    labourCompliance: "",
    complianceRemarks: "",
  });

  /* 06 Financial Information */
  const [financialInformation, setFinancialInformation] = useState({
    annualTurnover: "",
    bankName: "",
    bankAccountNumber: "",
    ifscCode: "",
    creditRating: "",
    financialRemarks: "",
  });

  /* 07 Commercial Information */
  const [commercialInformation, setCommercialInformation] = useState({
    paymentTerms: "",
    currency: "",
    priceList: "",
    warrantyTerms: "",
    commercialRemarks: "",
  });

  /* 08 Experience & References */
  const [experienceReferences, setExperienceReferences] = useState({
    yearsOfExperience: "",
    majorClients: "",
    referenceContact: "",
    referenceEmail: "",
    experienceRemarks: "",
  });

  /* 08b Customer Service & Support (General template only) */
  const [customerServiceSupport, setCustomerServiceSupport] = useState({});

  /* 10b Risk Management & Compliance (General template only) */
  const [riskCompliance, setRiskCompliance] = useState({});

  /* General Material Supplier Pre-Qualification questions are shown only when
   * the invitation template name starts with "General". Other templates keep
   * the existing tabs/questions unchanged. */
  const isGeneral = isGeneralTemplate(invitation?.template);

  const sectionStateSetters = {
    companyInformation: setCompanyInformation,
    qualityManagement: setQualityManagement,
    technicalCapability: setTechnicalCapability,
    deliveryCapability: setDeliveryCapability,
    complianceLegal: setComplianceLegal,
    financialInformation: setFinancialInformation,
    commercialInformation: setCommercialInformation,
    experienceReferences: setExperienceReferences,
    customerServiceSupport: setCustomerServiceSupport,
    riskCompliance: setRiskCompliance,
  };

  const sectionStateValues = {
    companyInformation,
    qualityManagement,
    technicalCapability,
    deliveryCapability,
    complianceLegal,
    financialInformation,
    commercialInformation,
    experienceReferences,
    customerServiceSupport,
    riskCompliance,
  };

  /* General template: tabs are exactly the template sheet sections (10 tabs,
   * template order) - the app-only Document Upload / Declaration tabs are not
   * shown. Every other template keeps the existing baseSections unchanged. */
  const sections = isGeneral ? getGeneralTemplateSections() : baseSections;

  const activeSectionIndex = sections.findIndex(
    (s) => s.id === activeSection
  );

  /* 09 Document Upload - same structure as Vendor Registration */
  const [isCardVisibleDoc, setCardVisibleDoc] = useState(true);
  const [documentTypeMaster, setDocumentTypeMaster] = useState([]);
  const [documents, setDocuments] = useState([
    {
      id: Date.now(),
      documentType: "",
      documentNumber: "",
      expirationDate: "",
      attachmentId: "",
    },
  ]);

  const toggleCardVisibilityDoc = () => {
    setCardVisibleDoc(!isCardVisibleDoc);
  };

  const addDocumentType = () => {
    const newDocument = {
      id: Date.now(),
      documentType: "",
      documentNumber: "",
      expirationDate: "",
      attachmentId: "",
    };
    setDocuments((prevData) => [...prevData, newDocument]);
  };

  const deleteDocumentType = (id) => {
    setDocuments((prevData) => prevData.filter((doc) => doc.id !== id));
  };

  const handleDocumentChange = (id, fieldName, value) => {
    setDocuments((prevData) =>
      prevData.map((doc) =>
        doc.id === id ? { ...doc, [fieldName]: value } : doc
      )
    );
  };

  const onSuccessUploadDocumentType = (data) => {
    setDocuments((prevData) =>
      prevData.map((doc) =>
        doc.id === data.fieldId
          ? { ...doc, attachmentId: data.attachmentId }
          : doc
      )
    );
  };

  /* 10 Declaration */
  const [declaration, setDeclaration] = useState({
    declared: false,
    authorisedPerson: "",
    designation: "",
    remarks: "",
  });

  useEffect(() => {
    const applyInvitation = (data) => {
      if (!data) return;
      const mapped = {
        ...defaultInvitation,
        ...data,
        userId:
          data.userId || data.invitationId || data.loginId || "",
      };
      setInvitation(mapped);
      setCompanyInformation((prev) => ({
        ...prev,
        companyName: mapped.companyName || prev.companyName || "",
      }));
      localStorage.setItem("vendorInvitation", JSON.stringify(mapped));
    };

    const stored = localStorage.getItem("vendorInvitation");
    let parsedInvitation = null;
    if (stored) {
      try {
        parsedInvitation = JSON.parse(stored);
        applyInvitation(parsedInvitation);
      } catch (e) {
        parsedInvitation = null;
      }
    }
    if (!parsedInvitation) {
      setCompanyInformation((prev) => ({
        ...prev,
        companyName: defaultInvitation.companyName,
      }));
    }

    /* Load invitation for logged-in temp-vendor (buyer localStorage won't exist here) */
    const loadInvitationFromApi = async () => {
      try {
        let userData = {};
        try {
          userData = JSON.parse(localStorage.getItem("userData") || "{}");
        } catch (e) {
          userData = {};
        }
        const userEmail = userData?.data?.email || "";
        const userCode = userData?.data?.userId || "";
        if (!userEmail && !userCode) return;

        const payload = userEmail
          ? { email: userEmail }
          : { userId: userCode };
        await ItemService.getPreQualificationList(payload).then((item) => {
          if (item?.status === 200 && item?.data?.length) {
            const latest =
              item.data.find((row) => row.status === "Invited") ||
              item.data[0];
            if (latest) {
              applyInvitation({
                companyName: latest.companyName || "",
                category: latest.category || "",
                subCategory: latest.subCategory || "",
                template: latest.template || "",
                email: latest.email || userEmail,
                submissionDeadline: latest.submissionDeadline || "",
                message: latest.message || "",
                userId:
                  latest.userId || latest.invitationId || userCode || "",
              });
            }
          }
        });
      } catch (e) {
        /* keep localStorage / default invitation */
      }
    };

    loadInvitationFromApi();
    getDocumentTypeMaster();
  }, []);

  const getDocumentTypeMaster = async () => {
    let obj = {
      codeType: "documentType",
    };
    await ItemService.getMasterDataSelf(obj).then((item) => {
      if (item?.status === 200) {
        setDocumentTypeMaster(item?.data);
      } else {
        setDocumentTypeMaster([]);
        console.log(">> Error: Loading getMasterDataSelf");
      }
    });
  };

  useEffect(() => {
    if (getDropdownData?.textField) {
      const setterMap = {
        companyType: setCompanyInformation,
        iso9001: setQualityManagement,
        qualityInspection: setQualityManagement,
        designCapability: setTechnicalCapability,
        gstRegistered: setComplianceLegal,
        creditRating: setFinancialInformation,
        paymentTerms: setCommercialInformation,
        currency: setCommercialInformation,
      };
      const generalKey = findGeneralQuestionSection(getDropdownData.textField);
      const setter =
        setterMap[getDropdownData.textField] ||
        (generalKey ? sectionStateSetters[generalKey] : null);
      if (setter) {
        setter((prev) => ({
          ...prev,
          [getDropdownData.textField]: getDropdownData.name,
        }));
      }
      setValidation((prev) => ({ ...prev, [getDropdownData.textField]: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getDropdownData]);

  const hideFailureModal = () => {
    setShowFailureModal(false);
  };

  const handleSectionInputChange = (setter) => ({ target: { name, value } }) => {
    setter((prev) => ({ ...prev, [name]: value }));
    if (validation[name]) {
      setValidation((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateSection = (sectionId) => {
    const errors = [];
    setValidation({});

    const sectionObj = sections.find((s) => s.id === sectionId);
    const sectionKey = sectionObj?.key;

    if (sectionKey === "companyInformation") {
      if (!companyInformation.companyName?.trim()) {
        errors.push(`Company Name: ${message.FILL_REQUIRED_FIELD}`);
        setValidation((p) => ({
          ...p,
          companyName: message.FILL_REQUIRED_FIELD,
        }));
      }
      if (!companyInformation.yearOfEstablishment?.trim()) {
        errors.push(`Year of Establishment: ${message.FILL_REQUIRED_FIELD}`);
        setValidation((p) => ({
          ...p,
          yearOfEstablishment: message.FILL_REQUIRED_FIELD,
        }));
      }
    }

    if (sectionKey === "declarationSubmit") {
      if (!declaration.declared) {
        errors.push("Please accept the declaration to submit.");
      }
      if (!declaration.authorisedPerson?.trim()) {
        errors.push(`Authorised Person Name: ${message.FILL_REQUIRED_FIELD}`);
        setValidation((p) => ({
          ...p,
          authorisedPerson: message.FILL_REQUIRED_FIELD,
        }));
      }
    }

    if (sectionKey === "documentUpload") {
      const missingDocs = documents.filter(
        (doc) => !doc.documentType?.trim() && !doc.attachmentId
      );
      if (documents.length === 0) {
        errors.push("Please add at least one document.");
      } else if (missingDocs.length > 0) {
        errors.push(
          `Please fill document type / upload file (${missingDocs.length} pending).`
        );
      }
    }

    /* General template - mandatory questions of the section being left */
    if (isGeneral) {
      const values = sectionKey ? sectionStateValues[sectionKey] || {} : {};
      getGeneralMandatoryQuestions(sectionKey).forEach((q) => {
        const value = values[q.name];
        if (value === undefined || value === null || String(value).trim() === "") {
          errors.push(`${q.label}: ${message.FILL_REQUIRED_FIELD}`);
          setValidation((p) => ({
            ...p,
            [q.name]: message.FILL_REQUIRED_FIELD,
          }));
        }
      });
    }

    if (errors.length > 0) {
      setErrorFields(errors);
      setShowFailureModal(true);
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (!validateSection(activeSection)) return;
    if (activeSectionIndex > -1 && activeSectionIndex < sections.length - 1) {
      setActiveSection(sections[activeSectionIndex + 1].id);
    }
  };

  const handlePrevious = () => {
    if (activeSectionIndex > 0) {
      setActiveSection(sections[activeSectionIndex - 1].id);
    }
  };

  const handleSectionClick = (sectionId) => {
    setActiveSection(sectionId);
  };

  const handleSaveDraft = () => {
    toast.success("Draft saved successfully!", {
      duration: 2000,
      position: "top-right",
    });
  };

  const handleSubmit = async () => {
    /* Declaration tab exists only for non-General templates (General template
     * follows the template sheet, which has no declaration / upload tabs). */
    const declarationSection = sections.find(
      (s) => s.key === "declarationSubmit"
    );
    if (declarationSection && !validateSection(declarationSection.id)) return;

    /* General template - final sweep for mandatory questions on every tab */
    if (isGeneral) {
      const missing = [];
      const generalValidation = {};
      let firstMissingSectionId = null;
      sections.forEach((section) => {
        const values = sectionStateValues[section.key] || {};
        getGeneralMandatoryQuestions(section.key).forEach((q) => {
          const value = values[q.name];
          if (
            value === undefined ||
            value === null ||
            String(value).trim() === ""
          ) {
            missing.push(`${q.label}: ${message.FILL_REQUIRED_FIELD}`);
            generalValidation[q.name] = message.FILL_REQUIRED_FIELD;
            if (firstMissingSectionId === null) {
              firstMissingSectionId = section.id;
            }
          }
        });
      });
      if (missing.length > 0) {
        setValidation((p) => ({ ...p, ...generalValidation }));
        setErrorFields(missing);
        setShowFailureModal(true);
        if (firstMissingSectionId !== null) {
          setActiveSection(firstMissingSectionId);
        }
        return;
      }
    }

    setDisabled(true);
    let userData = {};
    try {
      userData = JSON.parse(localStorage.getItem("userData") || "{}");
    } catch (e) {
      userData = {};
    }
    const payload = {
      invitation: {
        ...invitation,
        userId:
          invitation.userId ||
          invitation.invitationId ||
          invitation.loginId ||
          userData?.data?.userId ||
          "",
        email: invitation.email || userData?.data?.email || "",
      },
      companyInformation: {
        ...companyInformation,
        email: companyInformation.email || userData?.data?.email || "",
      },
      qualityManagement,
      technicalCapability,
      deliveryCapability,
      complianceLegal,
      financialInformation,
      commercialInformation,
      experienceReferences,
      customerServiceSupport,
      riskCompliance,
      /* General template has no Document Upload / Declaration tabs */
      documents: isGeneral ? [] : documents,
      declaration: isGeneral
        ? {
            declared: true,
            authorisedPerson:
              declaration.authorisedPerson ||
              userData?.data?.name ||
              userData?.data?.email ||
              invitation.email ||
              "",
            designation: declaration.designation || "",
            remarks:
              declaration.remarks || "General template - submitted by vendor",
          }
        : declaration,
      status: "Submitted",
    };

    try {
      await ItemService.submitPreQualification(payload).then((item) => {
        if (item?.status === 200) {
          setSubmitResult(item?.data?.[0] || null);
          setIsSubmitted(true);
          toast.success(item?.message || "Assessment submitted successfully!", {
            duration: 2000,
            position: "top-right",
          });
        } else {
          toast.error(item?.message || "Please try again !!", {
            duration: 4000,
            position: "top-right",
          });
        }
      });
    } catch (error) {
      toast.error("Please try again !!", {
        duration: 4000,
        position: "top-right",
      });
    } finally {
      setDisabled(false);
    }
  };

  const handleGoToAssessments = () => {
    navigate(SupplierConstants.VENDOR_MY_ASSESSMENTS);
  };

  /* ---------------- Section content renderers ---------------- */

  const renderCompanyInformation = () => (
    <Row className="pt-2 pb-4">
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Company Name"
          name="companyName"
          value={companyInformation.companyName || ""}
          onChange={handleSectionInputChange(setCompanyInformation)}
          required={true}
          error={validation.companyName}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Year of Establishment"
          name="yearOfEstablishment"
          type="number"
          value={companyInformation.yearOfEstablishment || ""}
          onChange={handleSectionInputChange(setCompanyInformation)}
          required={true}
          error={validation.yearOfEstablishment}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <ComboboxField
          label="Company Type"
          name="companyType"
          data={[
            { id: "1", name: "Private Limited" },
            { id: "2", name: "Public Limited" },
            { id: "3", name: "Partnership" },
            { id: "4", name: "Proprietorship" },
          ]}
          setValue={companyInformation.companyType}
          getvalue={setDropdownData}
          required={true}
          error={validation.companyType}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="PAN Number"
          name="panNumber"
          value={companyInformation.panNumber || ""}
          onChange={handleSectionInputChange(setCompanyInformation)}
          required={true}
          error={validation.panNumber}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="GST Number"
          name="gstNumber"
          value={companyInformation.gstNumber || ""}
          onChange={handleSectionInputChange(setCompanyInformation)}
          required={true}
          error={validation.gstNumber}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Website"
          name="website"
          value={companyInformation.website || ""}
          onChange={handleSectionInputChange(setCompanyInformation)}
        />
      </Col>
    </Row>
  );

  const renderQualityManagement = () => (
    <Row className="pt-2 pb-4">
      {/* <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <ComboboxField
          label="ISO 9001 Certified?"
          name="iso9001"
          data={[
            { id: "1", name: "Yes" },
            { id: "2", name: "No" },
          ]}
          setValue={qualityManagement.iso9001}
          getvalue={setDropdownData}
          required={true}
        />
      </Col> */}
      {/* <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="ISO Certificate Number"
          name="isoCertificateNumber"
          value={qualityManagement.isoCertificateNumber || ""}
          onChange={handleSectionInputChange(setQualityManagement)}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <ComboboxField
          label="Regular Quality Inspection?"
          name="qualityInspection"
          data={[
            { id: "1", name: "Yes" },
            { id: "2", name: "No" },
          ]}
          setValue={qualityManagement.qualityInspection}
          getvalue={setDropdownData}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Defect Rate (%)"
          name="defectRate"
          type="number"
          value={qualityManagement.defectRate || ""}
          onChange={handleSectionInputChange(setQualityManagement)}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <TextAreaField
          className="inputBox"
          label="Quality Policy"
          name="qualityPolicy"
          value={qualityManagement.qualityPolicy || ""}
          onChange={handleSectionInputChange(setQualityManagement)}
          style={{ minHeight: "80px" }}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <TextAreaField
          className="inputBox"
          label="Quality Remarks"
          name="qualityRemarks"
          value={qualityManagement.qualityRemarks || ""}
          onChange={handleSectionInputChange(setQualityManagement)}
          style={{ minHeight: "80px" }}
        />
      </Col> */}
    </Row>
  );

  const renderTechnicalCapability = () => (
    <Row className="pt-2 pb-4">
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <TextAreaField
          className="inputBox"
          label="Machinery & Equipment"
          name="machineryEquipment"
          value={technicalCapability.machineryEquipment || ""}
          onChange={handleSectionInputChange(setTechnicalCapability)}
          style={{ minHeight: "80px" }}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Production Capacity"
          name="productionCapacity"
          value={technicalCapability.productionCapacity || ""}
          onChange={handleSectionInputChange(setTechnicalCapability)}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Technical Team Strength"
          name="technicalTeamStrength"
          type="number"
          value={technicalCapability.technicalTeamStrength || ""}
          onChange={handleSectionInputChange(setTechnicalCapability)}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <ComboboxField
          label="In-house Design Capability?"
          name="designCapability"
          data={[
            { id: "1", name: "Yes" },
            { id: "2", name: "No" },
          ]}
          setValue={technicalCapability.designCapability}
          getvalue={setDropdownData}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <TextAreaField
          className="inputBox"
          label="Technical Remarks"
          name="technicalRemarks"
          value={technicalCapability.technicalRemarks || ""}
          onChange={handleSectionInputChange(setTechnicalCapability)}
          style={{ minHeight: "80px" }}
        />
      </Col>
    </Row>
  );

  const renderDeliveryCapability = () => (
    <Row className="pt-2 pb-4">
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <TextAreaField
          className="inputBox"
          label="Delivery Locations"
          name="deliveryLocations"
          value={deliveryCapability.deliveryLocations || ""}
          onChange={handleSectionInputChange(setDeliveryCapability)}
          style={{ minHeight: "80px" }}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Average Lead Time (Days)"
          name="averageLeadTime"
          type="number"
          value={deliveryCapability.averageLeadTime || ""}
          onChange={handleSectionInputChange(setDeliveryCapability)}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Warehouse Capacity"
          name="warehouseCapacity"
          value={deliveryCapability.warehouseCapacity || ""}
          onChange={handleSectionInputChange(setDeliveryCapability)}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Logistics Partner"
          name="logisticsPartner"
          value={deliveryCapability.logisticsPartner || ""}
          onChange={handleSectionInputChange(setDeliveryCapability)}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <TextAreaField
          className="inputBox"
          label="Delivery Remarks"
          name="deliveryRemarks"
          value={deliveryCapability.deliveryRemarks || ""}
          onChange={handleSectionInputChange(setDeliveryCapability)}
          style={{ minHeight: "80px" }}
        />
      </Col>
    </Row>
  );

  const renderComplianceLegal = () => (
    <Row className="pt-2 pb-4">
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <ComboboxField
          label="GST Registered?"
          name="gstRegistered"
          data={[
            { id: "1", name: "Yes" },
            { id: "2", name: "No" },
          ]}
          setValue={complianceLegal.gstRegistered}
          getvalue={setDropdownData}
          required={true}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Trade License Number"
          name="tradeLicense"
          value={complianceLegal.tradeLicense || ""}
          onChange={handleSectionInputChange(setComplianceLegal)}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Environmental Clearance"
          name="environmentalClearance"
          value={complianceLegal.environmentalClearance || ""}
          onChange={handleSectionInputChange(setComplianceLegal)}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Labour Compliance Status"
          name="labourCompliance"
          value={complianceLegal.labourCompliance || ""}
          onChange={handleSectionInputChange(setComplianceLegal)}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <TextAreaField
          className="inputBox"
          label="Compliance Remarks"
          name="complianceRemarks"
          value={complianceLegal.complianceRemarks || ""}
          onChange={handleSectionInputChange(setComplianceLegal)}
          style={{ minHeight: "80px" }}
        />
      </Col>
    </Row>
  );

  const renderFinancialInformation = () => (
    <Row className="pt-2 pb-4">
      {/* <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Annual Turnover"
          name="annualTurnover"
          value={financialInformation.annualTurnover || ""}
          onChange={handleSectionInputChange(setFinancialInformation)}
          required={true}
        />
      </Col> */}
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Bank Name"
          name="bankName"
          value={financialInformation.bankName || ""}
          onChange={handleSectionInputChange(setFinancialInformation)}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Bank Account Number"
          name="bankAccountNumber"
          value={financialInformation.bankAccountNumber || ""}
          onChange={handleSectionInputChange(setFinancialInformation)}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <ComboboxField
          label="Credit Rating"
          name="creditRating"
          data={[
            { id: "1", name: "AAA" },
            { id: "2", name: "AA" },
            { id: "3", name: "A" },
            { id: "4", name: "BBB" },
          ]}
          setValue={financialInformation.creditRating}
          getvalue={setDropdownData}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="IFSC Code"
          name="ifscCode"
          value={financialInformation.ifscCode || ""}
          onChange={handleSectionInputChange(setFinancialInformation)}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <TextAreaField
          className="inputBox"
          label="Financial Remarks"
          name="financialRemarks"
          value={financialInformation.financialRemarks || ""}
          onChange={handleSectionInputChange(setFinancialInformation)}
          style={{ minHeight: "80px" }}
        />
      </Col>
    </Row>
  );

  const renderCommercialInformation = () => (
    <Row className="pt-2 pb-4">
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <ComboboxField
          label="Payment Terms"
          name="paymentTerms"
          data={[
            { id: "1", name: "Net 30" },
            { id: "2", name: "Net 45" },
            { id: "3", name: "Net 60" },
            { id: "4", name: "Advance" },
          ]}
          setValue={commercialInformation.paymentTerms}
          getvalue={setDropdownData}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <ComboboxField
          label="Currency"
          name="currency"
          data={[
            { id: "1", name: "INR" },
            { id: "2", name: "USD" },
            { id: "3", name: "EUR" },
          ]}
          setValue={commercialInformation.currency}
          getvalue={setDropdownData}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Price List Reference"
          name="priceList"
          value={commercialInformation.priceList || ""}
          onChange={handleSectionInputChange(setCommercialInformation)}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Warranty Terms"
          name="warrantyTerms"
          value={commercialInformation.warrantyTerms || ""}
          onChange={handleSectionInputChange(setCommercialInformation)}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <TextAreaField
          className="inputBox"
          label="Commercial Remarks"
          name="commercialRemarks"
          value={commercialInformation.commercialRemarks || ""}
          onChange={handleSectionInputChange(setCommercialInformation)}
          style={{ minHeight: "80px" }}
        />
      </Col>
    </Row>
  );

  const renderExperienceReferences = () => (
    <Row className="pt-2 pb-4">
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Years of Experience"
          name="yearsOfExperience"
          type="number"
          value={experienceReferences.yearsOfExperience || ""}
          onChange={handleSectionInputChange(setExperienceReferences)}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <TextAreaField
          className="inputBox"
          label="Major Clients"
          name="majorClients"
          value={experienceReferences.majorClients || ""}
          onChange={handleSectionInputChange(setExperienceReferences)}
          style={{ minHeight: "80px" }}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Reference Contact Person"
          name="referenceContact"
          value={experienceReferences.referenceContact || ""}
          onChange={handleSectionInputChange(setExperienceReferences)}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Reference Email"
          name="referenceEmail"
          type="text"
          value={experienceReferences.referenceEmail || ""}
          onChange={handleSectionInputChange(setExperienceReferences)}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <TextAreaField
          className="inputBox"
          label="Experience Remarks"
          name="experienceRemarks"
          value={experienceReferences.experienceRemarks || ""}
          onChange={handleSectionInputChange(setExperienceReferences)}
          style={{ minHeight: "80px" }}
        />
      </Col>
    </Row>
  );

  const renderDocumentUpload = () => (
    <div className="accordion mb-4" id="docUploadAccordion">
      <div className="accordion-item">
        <h2 className="accordion-header">
          <Row className="accordion-button">
            <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
              <i
                className={`bi ${
                  isCardVisibleDoc ? "bi-chevron-right" : "bi-chevron-down"
                }`}
                onClick={toggleCardVisibilityDoc}
              ></i>
            </Col>
            <Col xs={1} md={1} lg={1} className="text-center logoBtn">
              <span className="iconLogo">CD</span>
            </Col>
            <Col xs={4} md={8} lg={8}>
              <span className="headerTitle">
                {" "}
                Company Documents & Certificates{" "}
              </span>
              <span className="subHeaderTitle">
                Company Documents & Certificates details
              </span>
            </Col>
            <Col xs={4} md={2} lg={2}>
              <div className="d-flex justify-content-end  p-2 me-3">
                <button className="btnTable" onClick={addDocumentType}>
                  Add Document
                </button>
              </div>
            </Col>
          </Row>
        </h2>
        <div
          className={`accordion-collapse collapse show ${
            isCardVisibleDoc ? "visible" : ""
          }`}
        >
          <div className="accordion-body">
            <Row>
              <Col xs={12} md={12} lg={12}>
                <div className="accordion-body">
                  <div className="d-flex-column justify-content-center">
                    <Row>
                      <Col xs={12}>
                        <div className="accordion-body">
                          <div className="d-flex-column justify-content-center">
                            <Row className="pt-4">
                              <Col xs={12} md={4} lg={2}>
                                <p className="fs-6 fw-medium d-flex justify-content-center">
                                  Document Type
                                </p>
                              </Col>
                              <Col xs={12} md={4} lg={2}>
                                <p className="fs-6 fw-medium d-flex ms-4">
                                  Document Number
                                </p>
                              </Col>
                              <Col xs={12} md={4} lg={2}>
                                <p className="fs-6 fw-medium d-flex ms-4">
                                  Expiration Date
                                </p>
                              </Col>
                              <Col xs={12} md={4} lg={1}>
                                <p className="fs-6 fw-medium d-flex ms-4">
                                  View Files
                                </p>
                              </Col>
                              <Col xs={12} md={4} lg={4}>
                                <p className="fs-6 fw-medium d-flex ms-4">
                                  Attachments
                                </p>
                              </Col>
                              <Col xs={12} md={4} lg={1}>
                                <p className="fs-6 fw-medium d-flex justify-content-center">
                                  Action
                                </p>
                              </Col>
                            </Row>
                            {documents.map((doc) => (
                              <Row key={doc.id}>
                                <Col xs={12}>
                                  <hr className="mx-4 my-0 text-color-black" />
                                </Col>
                                <Col
                                  xs={12}
                                  md={4}
                                  lg={2}
                                  className="commTopButtonRightLeftPadding"
                                >
                                  <ComboboxField
                                    label=""
                                    placeholder="Document type"
                                    data={documentTypeMaster}
                                    id="documentType"
                                    iconClassName="dropdownIcon"
                                    name="documentType"
                                    code="documentTypeCode"
                                    description="documentTypeDesc"
                                    setValue={doc?.documentType}
                                    getvalue={setDropdownData}
                                    index={doc.id}
                                    onChangeValue={handleDocumentChange}
                                  />
                                </Col>
                                <Col
                                  xs={12}
                                  md={4}
                                  lg={2}
                                  className="commTopButtonRightLeftPadding"
                                >
                                  <InputField
                                    className="inputBox"
                                    placeholder="Document Number"
                                    value={doc.documentNumber}
                                    onChange={(e) =>
                                      handleDocumentChange(
                                        doc.id,
                                        "documentNumber",
                                        e.target.value
                                      )
                                    }
                                  />
                                </Col>
                                <Col
                                  xs={12}
                                  md={4}
                                  lg={2}
                                  className="commTopButtonRightLeftPadding"
                                >
                                  <InputField
                                    className="inputBox"
                                    type="date"
                                    value={doc.expirationDate}
                                    onChange={(e) =>
                                      handleDocumentChange(
                                        doc.id,
                                        "expirationDate",
                                        e.target.value
                                      )
                                    }
                                  />
                                </Col>
                                <Col
                                  xs={12}
                                  md={4}
                                  lg={1}
                                  className="commTopButtonRightLeftPadding"
                                >
                                  <div className="d-flex justify-content-center">
                                    {(doc.attachmentId && (
                                      <img
                                        src="assets/img/newIcons/attached-file.png"
                                        className="mt-1 attachmentIcon"
                                        alt="attachments"
                                      />
                                    )) ||
                                      "--"}
                                  </div>
                                </Col>
                                <Col
                                  xs={12}
                                  md={4}
                                  lg={4}
                                  className="commTopButtonRightLeftPadding"
                                >
                                  <FileUploadComponent
                                    id={doc.id}
                                    name="iSOCertification"
                                    label=""
                                    allowedTypes={[
                                      "image/png",
                                      "image/jpeg",
                                      "application/pdf",
                                    ]}
                                    multiple={true}
                                    required={true}
                                    onSuccessUpload={
                                      onSuccessUploadDocumentType
                                    }
                                    value={doc.attachmentId || ""}
                                  />
                                </Col>
                                <Col
                                  xs={12}
                                  md={4}
                                  lg={1}
                                  className="commTopButtonRightLeftPadding"
                                >
                                  <div className="d-flex justify-content-center">
                                    <i
                                      onClick={() =>
                                        deleteDocumentType(doc.id)
                                      }
                                      className="bi bi-trash text-danger fs-4"
                                    />
                                  </div>
                                </Col>
                              </Row>
                            ))}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDeclarationSubmit = () => (
    <Row className="pt-2 pb-4">
      <Col xs={12} md={12} lg={12} className="commTopButtonRightLeftPadding">
        <div className="form-check mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="declarationCheck"
            checked={declaration.declared}
            onChange={(e) =>
              setDeclaration((prev) => ({
                ...prev,
                declared: e.target.checked,
              }))
            }
          />
          <label className="form-check-label" htmlFor="declarationCheck">
            I declare that the information provided above is true and correct to
            the best of my knowledge. I understand that any false information
            may result in disqualification.
          </label>
        </div>
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Authorised Person Name"
          name="authorisedPerson"
          value={declaration.authorisedPerson || ""}
          onChange={(e) =>
            setDeclaration((prev) => ({
              ...prev,
              authorisedPerson: e.target.value,
            }))
          }
          required={true}
          error={validation.authorisedPerson}
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <InputField
          className="inputBox"
          label="Designation"
          name="designation"
          value={declaration.designation || ""}
          onChange={(e) =>
            setDeclaration((prev) => ({
              ...prev,
              designation: e.target.value,
            }))
          }
        />
      </Col>
      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
        <TextAreaField
          className="inputBox"
          label="Remarks"
          name="remarks"
          value={declaration.remarks || ""}
          onChange={(e) =>
            setDeclaration((prev) => ({ ...prev, remarks: e.target.value }))
          }
          style={{ minHeight: "80px" }}
        />
      </Col>
    </Row>
  );

  /* ------- General template questions (config driven, General only) ------- */
  const renderGeneralFields = (sectionKey) => {
    const values = sectionStateValues[sectionKey] || {};
    const onChange = handleSectionInputChange(sectionStateSetters[sectionKey]);
    const items = getGeneralQuestions(sectionKey);

    return items.map((q) => {
      const required = isGeneralMandatory(q);
      const error = validation[q.name];
      const fullWidth = ["textarea", "table", "multi"].includes(q.type);
      /* wide answers take half a row, normal inputs a third - keeps the grid
       * aligned instead of one huge full-width block */
      const col = fullWidth
        ? { xs: 12, md: 4, lg: 4 }
        : { xs: 12, md: 4, lg: 4 };

      if (q.type === "yesno" || q.type === "yesno_na" || q.type === "select") {
        const options =
          q.type === "yesno"
            ? ["Yes", "No"]
            : q.type === "yesno_na"
            ? ["Yes", "No", "NA"]
            : q.options || [];
        return (
          <Col
            key={q.name}
            xs={col.xs}
            md={col.md}
            lg={col.lg}
            className="commTopButtonRightLeftPadding"
          >
            <ComboboxField
              label={q.label}
              name={q.name}
              data={options.map((o, i) => ({ id: String(i + 1), name: o }))}
              setValue={values[q.name] || ""}
              getvalue={setDropdownData}
              fireOnMount={false}
              required={required}
              error={error}
            />
          </Col>
        );
      }

      if (q.type === "yesno_details") {
        return (
          <React.Fragment key={q.name}>
            <Col
              xs={col.xs}
              md={col.md}
              lg={col.lg}
              className="commTopButtonRightLeftPadding"
            >
              <ComboboxField
                label={q.label}
                name={q.name}
                data={[
                  { id: "1", name: "Yes" },
                  { id: "2", name: "No" },
                ]}
                setValue={values[q.name] || ""}
                getvalue={setDropdownData}
                fireOnMount={false}
                required={required}
                error={error}
              />
            </Col>
            <Col
              xs={col.xs}
              md={col.md}
              lg={col.lg}
              className="commTopButtonRightLeftPadding"
            >
              <InputField
                className="inputBox"
                label={`${q.label} - Details`}
                name={`${q.name}Details`}
                value={values[`${q.name}Details`] || ""}
                onChange={onChange}
              />
            </Col>
          </React.Fragment>
        );
      }

      if (q.type === "upload") {
        return (
          <Col
            key={q.name}
            xs={12}
            md={4}
            lg={4}
            className="commTopButtonRightLeftPadding"
          >
            <FileUploadComponent
              id={q.name}
              name={q.name}
              label={q.label}
              allowedTypes={["image/png", "image/jpeg", "application/pdf"]}
              multiple={false}
              required={required}
              compact={true}
              value={values[q.name] || ""}
              onSuccessUpload={(data) => {
                sectionStateSetters[sectionKey]((prev) => ({
                  ...prev,
                  [q.name]: data?.attachmentId || "",
                }));
              }}
            />
          </Col>
        );
      }

      if (q.type === "auto_years") {
        const estYear =
          values.yearEstablished || companyInformation.yearOfEstablishment || "";
        const computed = estYear
          ? String(new Date().getFullYear() - Number(estYear))
          : "";
        return (
          <Col
            key={q.name}
            xs={col.xs}
            md={col.md}
            lg={col.lg}
            className="commTopButtonRightLeftPadding"
          >
            <InputField
              className="inputBox"
              label={q.label}
              name={q.name}
              value={computed}
              disabled={true}
            />
          </Col>
        );
      }

      if (fullWidth) {
        return (
          <Col
            key={q.name}
            xs={12}
            md={4}
            lg={4}
            className="commTopButtonRightLeftPadding"
          >
            <TextAreaField
              className="inputBox"
              label={q.label}
              name={q.name}
              value={values[q.name] || ""}
              onChange={onChange}
              style={{ minHeight: "80px" }}
              required={required}
              error={error}
              placeholder={
                q.type === "multi"
                  ? "Separate values with commas"
                  : q.type === "table"
                  ? "One entry per line"
                  : ""
              }
            />
          </Col>
        );
      }

      const inputType = ["number", "year", "currency", "percent"].includes(
        q.type
      )
        ? "number"
        : "text";
      return (
        <Col
          key={q.name}
          xs={col.xs}
          md={col.md}
          lg={col.lg}
          className="commTopButtonRightLeftPadding"
        >
          <InputField
            className="inputBox"
            label={q.label}
            name={q.name}
            type={inputType}
            value={values[q.name] || ""}
            onChange={onChange}
            required={required}
            error={error}
          />
        </Col>
      );
    });
  };

  const withGeneral = (sectionKey, content) => (
    <>
      {content}
      {isGeneral && getGeneralQuestions(sectionKey).length > 0 && (
        <Row className="pt-2 pb-4">{renderGeneralFields(sectionKey)}</Row>
      )}
    </>
  );

  const renderGeneralOnly = (sectionKey) =>
    isGeneral && getGeneralQuestions(sectionKey).length > 0 ? (
      <Row className="pt-2 pb-4">{renderGeneralFields(sectionKey)}</Row>
    ) : null;

  const renderSectionContent = () => {
    const sectionKey = sections.find((s) => s.id === activeSection)?.key;
    switch (sectionKey) {
      case "companyInformation":
        return withGeneral("companyInformation", renderCompanyInformation());
      case "qualityManagement":
        return withGeneral("qualityManagement", renderQualityManagement());
      case "technicalCapability":
        return withGeneral("technicalCapability", renderTechnicalCapability());
      case "deliveryCapability":
        return withGeneral("deliveryCapability", renderDeliveryCapability());
      case "complianceLegal":
        return withGeneral("complianceLegal", renderComplianceLegal());
      case "financialInformation":
        /* General template carries the bank / financial fields in its question
         * config, other templates use the static block below. */
        return isGeneral
          ? renderGeneralOnly("financialInformation")
          : renderFinancialInformation();
      case "commercialInformation":
        return withGeneral("commercialInformation", renderCommercialInformation());
      case "experienceReferences":
        return withGeneral("experienceReferences", renderExperienceReferences());
      case "customerServiceSupport":
        return renderGeneralOnly("customerServiceSupport");
      case "riskCompliance":
        return renderGeneralOnly("riskCompliance");
      case "documentUpload":
        return renderDocumentUpload();
      case "declarationSubmit":
        return renderDeclarationSubmit();
      default:
        return renderCompanyInformation();
    }
  };

  /* ---------------- Submitted success screen ---------------- */
  if (isSubmitted) {
    return (
      <main className="dashboard main" id="main">
        <Layout />
        <Row className="justify-center subContent">
          <Col xs={12} md={12} className="rightSide">
            <div className="rightSideInner PageCommonCSS">
              <h2 className="mainTitle">Assessment</h2>
              <p className="subText">Pre-Qualification Submission</p>
              <hr />
              <div className="text-center py-5">
                <i
                  className="bi bi-check2-circle text-success"
                  style={{ fontSize: "64px" }}
                ></i>
                <h3 className="mt-3">Assessment Submitted Successfully!</h3>
                <p className="text-muted">
                  Thank you for submitting the pre-qualification questionnaire.
                  <br />
                  Your application is now under review.
                </p>
              </div>
              <div className="accordion mb-4" id="successAccordion">
                <div className="accordion-item">
                  <div className="accordion-body">
                    <Row>
                      <Col xs={12} md={6} lg={6}>
                        <p className="mb-1">
                          <strong>Supplier ID:</strong>{" "}
                          {submitResult?.supplierId ||
                            submitResult?.userId ||
                            invitation?.loginId ||
                            "-"}
                        </p>
                        <p className="mb-1">
                          <strong>Reference No:</strong>{" "}
                          {submitResult?.referenceNumber || "-"}
                        </p>
                        <p className="mb-1">
                          <strong>Template:</strong>{" "}
                          {invitation?.template ||
                            defaultInvitation.template}
                        </p>
                      </Col>
                      <Col xs={12} md={6} lg={6}>
                        <p className="mb-1">
                          <strong>Submitted On:</strong>{" "}
                          {new Date().toLocaleString()}
                        </p>
                        <p className="mb-1">
                          <strong>Status:</strong>{" "}
                          <span className="badge bg-success">Registered</span>
                        </p>
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>
              <Row className="pb-4">
                <Col xs={12} className="text-center">
                  <button
                    className="btn btn-primary"
                    onClick={handleGoToAssessments}
                  >
                    Go to My Assessments
                  </button>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </main>
    );
  }

  const activeSectionObj = sections.find((s) => s.id === activeSection);

  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide">
          <div className="rightSideInner PageCommonCSS">
            <h2 className="mainTitle">
              {invitation?.template || defaultInvitation.template}
            </h2>
            <p className="subText">
              {invitation?.companyName || defaultInvitation.companyName} —{" "}
              {invitation?.category || defaultInvitation.category}
            </p>
            <hr />

            <div className="card">
              <div className="card-body">
                <ul className="nav nav-tabs" id="preQualTab" role="tablist">
                  {sections.map((section, sectionIdx) => {
                    const isActive = activeSection === section.id;
                    return (
                      <li
                        className="nav-item"
                        role="presentation"
                        key={section.id}
                      >
                        <button
                          className={`nav-link ${isActive ? "active" : ""}`}
                          id={`section-${section.id}-tab`}
                          type="button"
                          role="tab"
                          aria-controls={`section-${section.id}`}
                          aria-selected={isActive ? "true" : "false"}
                          onClick={() => handleSectionClick(section.id)}
                        >
                          <span className="me-1">{sectionIdx + 1}.</span>
                          {section.label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <div className="tab-content pt-3" id="preQualTabContent">
                  <div
                    className={`tab-pane fade show active`}
                    id={`section-${activeSection}`}
                    role="tabpanel"
                    aria-labelledby={`section-${activeSection}-tab`}
                  >
                    <div className="card">
                      <div className="card-header bg-white">
                        <strong>
                          {activeSectionIndex + 1}. {activeSectionObj?.label}
                        </strong>
                      </div>
                      <div className="card-body">
                        {renderSectionContent()}
                      </div>
                      <div className="card-footer bg-white">
                        <Row className="py-2">
                          <Col
                            xs={12}
                            md={2}
                            lg={2}
                            className="commTopButtonRightLeftPadding"
                          >
                            <button
                              className="btnTable"
                              onClick={handlePrevious}
                              disabled={activeSection === 1}
                            >
                              <i className="bi bi-arrow-left"></i> Previous
                            </button>
                          </Col>
                          <Col
                            xs={12}
                            md={2}
                            lg={2}
                            className="commTopButtonRightLeftPadding"
                          >
                            <button
                              className="btnTable"
                              onClick={handleSaveDraft}
                            >
                              <i className="bi bi-save"></i> Save as Draft
                            </button>
                          </Col>
                          <Col
                            xs={12}
                            md={{ span: 2, offset: 10 }}
                            lg={{ span: 2, offset: 10 }}
                            className="commTopButtonRightLeftPadding"
                          >
                            {activeSectionIndex < sections.length - 1 ? (
                              <button
                                className="btnTable"
                                onClick={handleNext}
                              >
                                Next <i className="bi bi-arrow-right"></i>
                              </button>
                            ) : (
                              <button
                                className="btnTable"
                                onClick={handleSubmit}
                                disabled={disabled}
                              >
                                <i className="bi bi-check-lg"></i> Submit
                              </button>
                            )}
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <FailureModal
        show={getShowFailureModal}
        onHide={() => hideFailureModal()}
        failuremodaldata={getFailureModal}
        requiredfields={getErrorFields}
      />
    </main>
  );
};

export default VendorPreQualificationForm;
